<template>
  <div :class="style.block">
    <InputStyle
      description="Заголовок"
      placeholder="Введите заголовок"
      size="M"
      textarea
      :modelValue="headerText"
      @input="changeHeaderText"
    />
  </div>

  <div :class="style.block">
    <InputStyle
      description="Описание"
      placeholder="Введите описание"
      size="M"
      textarea
      :modelValue="paragraphText"
      @input="changeParagraphText"
    />
  </div>

  <div :class="style.block">
    <InputFieldMedia
      description="Медиа"
      :text="imageText"
      :image="thankYouImg"
      isFile
      :mediaType="thankYouMediaType"
      :actions="actions"
      @upload="openFileDialog"
    />

    <input
      ref="fileInput"
      :class="style.hiddenInput"
      type="file"
      accept="image/*, video/*"
      @change="changeImage"
    />
  </div>
</template>

<script lang="ts">
import style from "./ThankYouPageSettingComponent.module.scss";
import {defineComponent, markRaw} from "vue";

import InputFieldMedia from "@/elements/UI/input/Media/InputFieldMedia.vue";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";

import {questions, Quiz} from "@/page/quiz/typeQuiz";
import {modifyQuiz} from "@/page/quiz/scriptQuiz";
import {DOMAIN} from "@/http";

import uploadIcon from "$images/icons/button/Name=Upload, Size=16, Tone=Default.svg";
import deleteIcon from "$images/icons/button/Name=Delete, Size=16, Tone=Error.svg";
import GhostButtonError from "@/elements/UI/button/ButtonStyle/Error/ghost/GhostButtonError.vue";

export default defineComponent({
  name: "ThankYouPageSettingComponent",

  components: {
    InputStyle,
    InputFieldMedia
  },

  emits: ["closePanel"],

  data() {
    return {
      style,
      modifyQuiz,
      DOMAIN,
      localPreviewUrl: "",
      cachedPreviewUrls: {} as Record<string, string>,
      localThankYouMediaType: "" as "image" | "video" | ""
    };
  },

  beforeUnmount() {
    this.revokeLocalPreview();
    this.revokeCachedPreviews();
  },

  computed: {
    quiz(): Quiz {
      return this.$quizStore.getQuiz;
    },

    questions(): questions[] {
      const value = this.modifyQuiz("get", "", ["questions"]);

      return Array.isArray(value) ? value : [];
    },

    cachedFiles(): File[] {
      return this.$quizStore.getCachedFiles;
    },

    thankYouQuestionIndex(): number {
      if (!this.questions.length) {
        return -1;
      }

      return this.questions.length - 1;
    },

    thankYouQuestion(): questions | null {
      if (this.thankYouQuestionIndex === -1) {
        return null;
      }

      return this.questions[this.thankYouQuestionIndex] ?? null;
    },

    headerText(): string {
      const value = this.thankYouQuestion?.question;

      return typeof value === "string" ? value : "";
    },

    paragraphText(): string {
      const value =
        this.thankYouQuestion?.contacts?.paragraph ??
        this.thankYouQuestion?.paragraph;

      return typeof value === "string" ? value : "";
    },

    rawThankYouImg(): string {
      const value = this.thankYouQuestion?.thankYouImg;

      return typeof value === "string" ? value : "";
    },

    thankYouImg(): string {
      if (this.localPreviewUrl) {
        return `url('${this.localPreviewUrl}')`;
      }

      if (!this.rawThankYouImg) {
        return "";
      }

      if (
        this.rawThankYouImg.startsWith("blob:") ||
        this.rawThankYouImg.startsWith("data:")
      ) {
        return `url('${this.rawThankYouImg}')`;
      }

      const cachedPreviewUrl = this.getCachedPreviewUrl(this.rawThankYouImg);

      if (cachedPreviewUrl) {
        return `url('${cachedPreviewUrl}')`;
      }

      return `url('https://${this.DOMAIN}/api/img?name=${this.rawThankYouImg}')`;
    },

    hasImage(): boolean {
      return Boolean(this.rawThankYouImg);
    },

    thankYouMediaType(): "image" | "video" {
      if (this.localThankYouMediaType) {
        return this.localThankYouMediaType;
      }

      return this.isVideoPath(this.rawThankYouImg) ? "video" : "image";
    },

    imageText(): string {
      return "Файл";
    },

    actions(): ListActionItem[] {
      return [
        {
          text: "Загрузить",
          value: "upload",
          icon: uploadIcon,
          variant: "tab",
          disabled: false,
          handler: this.openFileDialog
        },
        {
          text: "Удалить",
          value: "delete",
          icon: deleteIcon,
          variant: "button",
          buttonComponent: markRaw(GhostButtonError),
          disabled: !this.hasImage,
          handler: this.deleteImage
        }
      ];
    }
  },

  methods: {
    openFileDialog(): void {
      const fileInput = this.$refs.fileInput as HTMLInputElement | undefined;

      if (!fileInput) {
        return;
      }

      fileInput.click();
    },

    changeImage(event: Event): void {
      if (this.thankYouQuestionIndex === -1 || !this.thankYouQuestion) {
        return;
      }

      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (!file) {
        return;
      }

      const newFile = this.createThankYouImageFile(file);
      const previewUrl = URL.createObjectURL(newFile);

      this.revokeLocalPreview();
      this.revokeCachedPreview(this.rawThankYouImg);
      this.removeOldThankYouImageFromCachedFiles(newFile.name);

      this.cachedFiles.push(newFile);

      this.localPreviewUrl = previewUrl;
      this.thankYouQuestion.thankYouImg = newFile.name;
      this.localThankYouMediaType = this.isVideoFile(file) ? "video" : "image";

      target.value = "";
    },

    deleteImage(): void {
      if (
        this.thankYouQuestionIndex === -1 ||
        !this.thankYouQuestion ||
        !this.hasImage
      ) {
        return;
      }

      this.revokeLocalPreview();
      this.revokeCachedPreview(this.rawThankYouImg);
      this.removeOldThankYouImageFromCachedFiles();

      this.thankYouQuestion.thankYouImg = "";
      this.localThankYouMediaType = "";
    },

    changeHeaderText(event: Event): void {
      if (this.thankYouQuestionIndex === -1) {
        return;
      }

      const target = event.target as HTMLInputElement;

      this.modifyQuiz("set", target.value, [
        "questions",
        this.thankYouQuestionIndex,
        "question"
      ]);
    },

    changeParagraphText(event: Event): void {
      if (this.thankYouQuestionIndex === -1) {
        return;
      }

      const target = event.target as HTMLInputElement;

      this.modifyQuiz("set", target.value, [
        "questions",
        this.thankYouQuestionIndex,
        "contacts",
        "paragraph"
      ]);
    },

    createThankYouImageFile(file: File): File {
      const nameParts = file.name.split(".");
      const extension = nameParts[nameParts.length - 1];
      const questionType = this.thankYouQuestion?.type ?? "thankYouPage";
      const newName = `${this.thankYouQuestionIndex}-${questionType}.${extension}`;
      const blob = file.slice(0, file.size, file.type);

      return new File([blob], newName, {
        type: file.type
      });
    },

    isVideoFile(file: File): boolean {
      return file.type.startsWith("video/") || this.isVideoPath(file.name);
    },

    isVideoPath(path = ""): boolean {
      return /\.(mp4|webm|ogg)(?:$|[?#])/i.test(path);
    },

    getCachedPreviewUrl(fileName: string): string {
      if (!fileName) {
        return "";
      }

      const currentPreviewUrl = this.cachedPreviewUrls[fileName];

      if (currentPreviewUrl) {
        return currentPreviewUrl;
      }

      const cachedFile = this.cachedFiles.find((file) => {
        return file.name === fileName;
      });

      if (!cachedFile) {
        return "";
      }

      const previewUrl = URL.createObjectURL(cachedFile);

      this.cachedPreviewUrls = {
        ...this.cachedPreviewUrls,
        [fileName]: previewUrl
      };

      return previewUrl;
    },

    revokeLocalPreview(): void {
      if (this.localPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.localPreviewUrl);
      }

      this.localPreviewUrl = "";
    },

    revokeCachedPreview(fileName: string): void {
      if (!fileName) {
        return;
      }

      const previewUrl = this.cachedPreviewUrls[fileName];

      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      const nextPreviewUrls = {...this.cachedPreviewUrls};

      delete nextPreviewUrls[fileName];

      this.cachedPreviewUrls = nextPreviewUrls;
    },

    revokeCachedPreviews(): void {
      Object.values(this.cachedPreviewUrls).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });

      this.cachedPreviewUrls = {};
    },

    removeOldThankYouImageFromCachedFiles(nextFileName?: string): void {
      if (!this.thankYouQuestion) {
        return;
      }

      const currentType = this.thankYouQuestion.type ?? "thankYouPage";
      const imagePrefix = `${this.thankYouQuestionIndex}-${currentType}.`;

      for (let index = this.cachedFiles.length - 1; index >= 0; index -= 1) {
        const cachedFile = this.cachedFiles[index];

        if (!cachedFile.name.startsWith(imagePrefix)) {
          continue;
        }

        if (nextFileName && cachedFile.name === nextFileName) {
          this.cachedFiles.splice(index, 1);
          continue;
        }

        if (!nextFileName) {
          this.cachedFiles.splice(index, 1);
        }
      }
    }
  },

  watch: {
    rawThankYouImg(newValue: string, oldValue: string): void {
      if (oldValue && oldValue !== newValue) {
        this.revokeCachedPreview(oldValue);
      }

      if (!newValue) {
        this.revokeLocalPreview();
        this.localThankYouMediaType = "";
        return;
      }

      if (newValue !== this.localPreviewUrl) {
        this.localPreviewUrl = "";
      }
    },

    cachedFiles(): void {
      this.revokeCachedPreviews();
    }
  }
});
</script>
